import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  CpuChipIcon,
  PaperAirplaneIcon,
  UserIcon,
  XMarkIcon,
  PhotoIcon,
  CurrencyDollarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { IProjectAsset, AspectRatioLabels, Resolution, ResolutionLabels, getLabelsByLocale, Locale } from '@packages/global';
import { ModelType } from '@packages/global';
import { useModelStore } from '../../../stores/modelStore';
import { CompactModelSelector } from '../../common/form';
import AttachmentCard from './AttachmentCard';
import StyleSelector from './StyleSelector';

const EPISODE_SCRIPT_GENERATION_PRICE = 20;

interface SelectedModels {
  llmModelId: string;
  llmVlModelId: string;
  text2imageModelId: string;
  imageEditModelId: string;
}

interface VideoGenerationTabProps {
  prompt: string;
  onPromptChange: (v: string) => void;
  loading: boolean;
  uploadedFileName: string;
  uploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFile: () => void;
  selectedSubjects: IProjectAsset[];
  onOpenSubjectSelector: () => void;
  onRemoveSubject: (id: string) => void;
  selectedStyle: string;
  onStyleChange: (s: string) => void;
  targetDuration: number | null;
  onTargetDurationChange: (v: number | null) => void;
  maxShotDuration: number | null;
  onMaxShotDurationChange: (v: number | null) => void;
  aspectRatio: string;
  onAspectRatioChange: (v: string) => void;
  resolution: Resolution;
  onResolutionChange: (v: Resolution) => void;
  selectedModels: SelectedModels;
  onModelsChange: (models: SelectedModels) => void;
  hasEnoughCoins: boolean;
  onSubmit: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
}

export default function VideoGenerationTab({
  prompt, onPromptChange, loading,
  uploadedFileName, uploading, fileInputRef, onFileUpload, onClearFile,
  selectedSubjects, onOpenSubjectSelector, onRemoveSubject,
  selectedStyle, onStyleChange,
  targetDuration, onTargetDurationChange,
  maxShotDuration, onMaxShotDurationChange,
  aspectRatio, onAspectRatioChange,
  resolution, onResolutionChange,
  selectedModels, onModelsChange,
  hasEnoughCoins, onSubmit, onKeyPress
}: VideoGenerationTabProps) {
  const { getModelsByType } = useModelStore();
  const { t, i18n } = useTranslation(['script', 'common']);
  const aspectRatioLabels = getLabelsByLocale(AspectRatioLabels, i18n.language as Locale);
  const resolutionLabels = getLabelsByLocale(ResolutionLabels, i18n.language as Locale);
  const llmModels = getModelsByType(ModelType.LLM);
  const llmVlModels = getModelsByType(ModelType.LLM_VL);
  const text2imageModels = getModelsByType(ModelType.TEXT_TO_IMAGE);
  const imageEditModels = getModelsByType(ModelType.IMAGE_EDIT);

  const [showModelSelector, setShowModelSelector] = React.useState(false);
  const [showAspectRatioSelector, setShowAspectRatioSelector] = React.useState(false);
  const [showResolutionSelector, setShowResolutionSelector] = React.useState(false);
  const [showDurationPanel, setShowDurationPanel] = React.useState(false);
  const modelSelectorRef = useRef<HTMLDivElement>(null);
  const aspectRatioRef = useRef<HTMLDivElement>(null);
  const resolutionRef = useRef<HTMLDivElement>(null);
  const durationPanelRef = useRef<HTMLDivElement>(null);

  const hasMultipleModels = llmModels.length > 1 || llmVlModels.length > 1 || text2imageModels.length > 1 || imageEditModels.length > 1;

  // 时长按钮的摘要文字
  const durationSummary = (() => {
    const parts: string[] = [];
    if (targetDuration) parts.push(t('videoGeneration.durationSeconds', { value: targetDuration }));
    if (maxShotDuration) parts.push(t('videoGeneration.maxShotDurationSeconds', { value: maxShotDuration }));
    return parts.length > 0 ? parts.join(' · ') : t('videoGeneration.duration');
  })();
  const hasDurationActive = !!(targetDuration || maxShotDuration);

  return (
    <>
      <input ref={fileInputRef} type="file" accept=".txt,.md,.docx" onChange={onFileUpload} className="hidden" />

      {/* 附件卡片 + Prompt 并排 */}
      <div className="flex items-start gap-3 pt-1">
        <AttachmentCard
          fileName={uploadedFileName}
          uploading={uploading}
          disabled={loading}
          required={false}
          iconType="document"
          label={t('videoGeneration.singleEpisodeScript')}
          tooltip={t('videoGeneration.singleEpisodeTooltip')}
          onUploadClick={() => fileInputRef.current?.click()}
          onClear={onClearFile}
        />
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) onKeyPress(e as any); }}
          placeholder={t('videoGeneration.promptPlaceholder')}
          className="flex-1 bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-base resize-none h-24 py-0"
          disabled={loading}
          style={{ opacity: loading ? 0.6 : 1 }}
        />
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* 模型选择 */}
          {hasMultipleModels && (
            <div className="relative" ref={modelSelectorRef}>
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
              >
                <CpuChipIcon className="w-3.5 h-3.5 text-green-400" />
                <span>{t('idea.model')}</span>
                <ChevronDownIcon className="w-3 h-3 text-gray-400" />
              </button>
              {showModelSelector && (
                <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] p-4 min-w-[320px]">
                  <div className="space-y-3">
                    {llmModels.length > 1 && (
                      <CompactModelSelector label={t('idea.llmModel')} models={llmModels} value={selectedModels.llmModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, llmModelId: id })} />
                    )}
                    {llmVlModels.length > 1 && (
                      <CompactModelSelector label={t('idea.visionLlmModel')} models={llmVlModels} value={selectedModels.llmVlModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, llmVlModelId: id })} />
                    )}
                    {text2imageModels.length > 1 && (
                      <CompactModelSelector label={t('idea.imageGenModel')} models={text2imageModels} value={selectedModels.text2imageModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, text2imageModelId: id })} />
                    )}
                    {imageEditModels.length > 1 && (
                      <CompactModelSelector label={t('idea.imageEditModel')} models={imageEditModels} value={selectedModels.imageEditModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, imageEditModelId: id })} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 主体选择 */}
          <button
            onClick={onOpenSubjectSelector}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
          >
            <UserIcon className="w-3.5 h-3.5 text-primary" />
            <span>{selectedSubjects.length > 0 ? t('idea.subjectWithCount', { count: selectedSubjects.length }) : t('idea.subject')}</span>
          </button>

          {/* 风格 */}
          <StyleSelector selectedStyle={selectedStyle} onStyleChange={onStyleChange} disabled={loading} />

          {/* 时长设置（视频时长 + 分镜时长合并面板） */}
          <div className="relative" ref={durationPanelRef}>
            <button
              onClick={() => setShowDurationPanel(!showDurationPanel)}
              disabled={loading}
              className={`flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-[13px] rounded-lg border transition-colors disabled:opacity-50 ${hasDurationActive ? 'text-amber-400 border-amber-400/40' : 'text-gray-300 border-border-subtle'}`}
            >
              <ClockIcon className="w-3.5 h-3.5 text-amber-400" />
              <span>{durationSummary}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>

            {showDurationPanel && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] p-4 min-w-[280px]">
                <div className="space-y-4">

                  {/* 视频总时长 */}
                  <div>
                    <div className="mb-1.5">
                      <span className="text-[13px] font-medium text-gray-200">{t('videoGeneration.durationPanelLabel')}</span>
                      <p className="text-[11px] text-gray-500 mt-0.5">{t('videoGeneration.durationPanelDesc')}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {[null, 15, 30, 60, 90, 120].map((val) => (
                        <button
                          key={val ?? 'auto'}
                          onClick={() => onTargetDurationChange(val)}
                          className={`px-2.5 py-1 text-[12px] rounded-md border transition-colors cursor-pointer ${targetDuration === val
                              ? 'bg-amber-400/15 text-amber-400 border-amber-400/40'
                              : 'bg-bg-elevated text-gray-300 border-border-subtle hover:border-border-default'
                            }`}
                        >
                          {val === null ? t('videoGeneration.durationAuto') : t('videoGeneration.durationSecondsSuffix', { value: val })}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-border-subtle" />

                  {/* 单镜最大时长 */}
                  <div>
                    <div className="mb-1.5">
                      <span className="text-[13px] font-medium text-gray-200">{t('videoGeneration.maxShotDurationLabel')}</span>
                      <p className="text-[11px] text-gray-500 mt-0.5">{t('videoGeneration.maxShotDurationDesc')}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {[null, 2.5, 5, 7.5, 10, 12.5, 15].map((val) => (
                        <button
                          key={val ?? 'no-limit'}
                          onClick={() => onMaxShotDurationChange(val)}
                          className={`px-2.5 py-1 text-[12px] rounded-md border transition-colors cursor-pointer ${maxShotDuration === val
                              ? 'bg-amber-400/15 text-amber-400 border-amber-400/40'
                              : 'bg-bg-elevated text-gray-300 border-border-subtle hover:border-border-default'
                            }`}
                        >
                          {val === null ? t('videoGeneration.maxShotDurationNoLimit') : t('videoGeneration.durationSecondsSuffix', { value: val })}
                        </button>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>

          {/* 画面比例 */}
          <div className="relative" ref={aspectRatioRef}>
            <button
              onClick={() => setShowAspectRatioSelector(!showAspectRatioSelector)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
            >
              <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-purple-400" />
              <span>{aspectRatio}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showAspectRatioSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[160px]">
                {Object.entries(aspectRatioLabels).map(([value, label]) => (
                  <button key={value} onClick={() => { onAspectRatioChange(value); setShowAspectRatioSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${aspectRatio === value ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 分辨率 */}
          <div className="relative" ref={resolutionRef}>
            <button
              onClick={() => setShowResolutionSelector(!showResolutionSelector)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
            >
              <PhotoIcon className="w-3.5 h-3.5 text-blue-400" />
              <span>{resolutionLabels[resolution]}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showResolutionSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[140px]">
                {Object.entries(resolutionLabels).map(([value, label]) => (
                  <button key={value} onClick={() => { onResolutionChange(value as Resolution); setShowResolutionSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${resolution === value ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 价格 + 发送 */}
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-3 py-2 text-[14px] rounded-lg ${hasEnoughCoins ? 'text-gray-400' : 'text-red-400'}`}>
            <CurrencyDollarIcon className="w-4 h-4" />
            <span>{EPISODE_SCRIPT_GENERATION_PRICE}</span>
          </div>
          <button
            onClick={onSubmit}
            disabled={loading || !prompt.trim() || !hasEnoughCoins}
            className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary-dark hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={!hasEnoughCoins ? t('idea.insufficientCoins', { amount: EPISODE_SCRIPT_GENERATION_PRICE }) : ''}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
            )}
          </button>
        </div>
      </div>

      {/* 已选主体 */}
      {selectedSubjects.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border-subtle">
          <div className="flex items-center gap-2 flex-wrap">
            {selectedSubjects.map((asset) => (
              <div key={asset.id} className="flex items-center gap-1 px-2 py-1 bg-bg-elevated rounded-lg text-xs text-gray-300">
                {asset.conceptImageUrl ? (
                  <img src={asset.conceptImageUrl} alt={asset.name} className="w-4 h-4 object-cover rounded" />
                ) : (
                  <UserIcon className="w-4 h-4 text-gray-400" />
                )}
                <span className="truncate max-w-16">{asset.name}</span>
                <button onClick={() => onRemoveSubject(asset.id)} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
